"use client";

import { useLanguage } from "../i18n/LanguageProvider";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* ============================================================
   CENTER OF EXCELLENCE - CAROUSEL FASILITAS PENUNJANG

   >>> KENAPA BERKAS TERPISAH, TIDAK DITEMPEL DI page.jsx About <<<

   Carousel ini butuh useState/useRef/onClick, jadi harus "use client".
   Halaman About mengekspor `metadata`, dan itu HANYA boleh ada di
   server component. Dua-duanya tidak bisa hidup di satu berkas -
   build akan gagal.

   ============================================================
   GAMBAR

   Path tiap foto ada di array FASILITAS di bawah, mengikuti berkas
   yang sudah ada di public/images/layanan/. Selama berkasnya belum
   ada, kartu menampilkan kotak penampung yang MENULISKAN SENDIRI nama
   berkas yang ia tunggu.

   UKURAN YANG DISARANKAN: 1200 x 750 px (rasio 16:10). Foto berasio
   lain tetap tampil, hanya terpotong di sisi yang berlebih.

   NAMA BERKAS TANPA SPASI. Di public/images/ ada berkas bernama
   "logo rs.png" - spasi harus di-encode jadi %20 dan sebagian tooling
   deploy menanganinya tidak konsisten.

   ============================================================
   ⚠️ TEKS DESKRIPSI MASIH PERLU DISETUJUI TIM RS

   Kalimat tiap kartu ditulis NETRAL - apa fasilitasnya, bukan
   seberapa bagus. Tidak ada angka, janji hasil, atau kata
   "terbaik"/"tercepat". Klaim superlatif pada layanan kesehatan masuk
   wilayah yang diatur.

   Tetap COCOKKAN SATU PER SATU dengan layanan yang benar-benar ada.
   ============================================================ */


/* ============================================================
   JARAK TEPI

   Judul, track kartu, dan baris tombol memakai jarak tepi yang sama:
   16px di ponsel, 24px mulai sm, 40px mulai lg.

   Nilainya ditulis sebagai CSS biasa yang disisipkan komponen ini,
   bukan utilitas Tailwind. Itu memutus tiga kemungkinan penyebab
   sekaligus: kelas yang tidak ikut ter-generate saat build, nilai
   breakpoint yang di-override di tailwind.config, dan perilaku
   padding pada flex container ber-overflow-x.

   >>> REVISI: TRACK TIDAK LAGI FULL-BLEED

   Versi-versi sebelumnya membuat kartu membentang sampai tepi layar
   dan hanya BERHENTI di garis judul saat digulir mentok. Di tengah
   guliran, kartu tetap terlihat sampai pojok layar.

   Sekarang area gulirnya sendiri yang dipersempit: track dibungkus
   satu <div class="coe-pad">, jadi kotak scroll-nya MULAI dan
   BERAKHIR tepat di garis judul. Kartu yang melewati garis itu
   langsung terpotong - tidak pernah terlihat di luar kolom, di posisi
   gulir mana pun.

   YANG IKUT BERUBAH: isyarat "masih ada lagi ke samping" tidak lagi
   datang dari kartu yang terpotong di tepi layar, melainkan dari
   kartu yang terpotong di garis kanan kolom. Lebih halus, tapi masih
   ada - dan panah di bawah tetap menegaskannya.

   SPACER DAN scroll-padding SUDAH TIDAK DIPERLUKAN, jadi keduanya
   dihapus. Tepi kotak scroll kini berada tepat di garis judul, jadi
   snap-start berhenti di sana dengan sendirinya. Menambahkannya lagi
   justru menggeser kartu dua kali.

   UNTUK MENGUBAH JARAKNYA: ubah angka di JARAK saja.
   ============================================================ */
const JARAK = { base: 16, sm: 24, lg: 40 };

const CSS_JARAK = `
.coe-pad { padding-left: ${JARAK.base}px; padding-right: ${JARAK.base}px; }

@media (min-width: 640px) {
  .coe-pad { padding-left: ${JARAK.sm}px; padding-right: ${JARAK.sm}px; }
}

@media (min-width: 1024px) {
  .coe-pad { padding-left: ${JARAK.lg}px; padding-right: ${JARAK.lg}px; }
}
`;

const LIHAT_SEMUA_HREF = "";

const AJAK_HREF = "/janji-temu";

const FASILITAS = [
  
  { key: "ambulan", image: "/images/layanan/ambulance.png", href: "", fokus: "50% 96%" },
  { key: "laboratorium", image: "/images/layanan/lab.jpg", href: "/layanan/laboratorium" },
  { key: "apotek", image: "/images/layanan/apotek.png", href: "/layanan/apotek" },
  { key: "rawatInap", image: "/images/layanan/ranap.jpg", href: "/layanan/rawat-inap" },
  { key: "igd", image: "/images/layanan/igd.jpg", href: "/layanan/igd" },
  { key: "mcu", image: "/images/layanan/medical-check-up.png", href: "/layanan/mcu" },
  { key: "rehabilitasi", image: "/images/layanan/rehab.jpg", href: "" },
  { key: "radiologi", image: "/images/layanan/radiologi.jpg", href: "" },
  { key: "delivery", image: "/images/layanan/delivery.png", href: "/layanan/delivery-obat" },
  { key: "atm", image: "/images/layanan/atm.jpg", href: "" },
  { key: "mushola", image: "/images/layanan/mushola.jpg", href: "" },
  { key: "wifi", image: "/images/layanan/free-wifi.png", href: "" },
  { key: "kafe", image: "/images/layanan/kantin.jpg", href: "" },
  { key: "taman", image: "/images/layanan/taman-bermain.anak.jpg", href: "" },
];


export default function CenterOfExcellence() {
  const { t } = useLanguage();

  const trackRef = useRef(null);
  const cardRefs = useRef([]);

  const [nav, setNav] = useState({ atStart: true, atEnd: false });


  const syncEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;

    setNav({
      atStart: track.scrollLeft <= 4,
      atEnd: max <= 0 || track.scrollLeft >= max - 4,
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    syncEdges();
    track.addEventListener("scroll", syncEdges, { passive: true });
    window.addEventListener("resize", syncEdges);
    return () => {
      track.removeEventListener("scroll", syncEdges);
      window.removeEventListener("resize", syncEdges);
    };
  }, [syncEdges]);

  /* Langkah diukur dari selisih offsetLeft antara kartu pertama dan
     kedua, bukan offsetWidth + angka gap yang ditulis manual - kalau
     manual, tiap tekan meleset di breakpoint yang gap-nya berbeda.

     Spacer TIDAK masuk cardRefs (ia di luar .map), jadi perhitungan
     ini tidak terpengaruh olehnya. */
  const scrollByCard = (arah) => {
    const track = trackRef.current;
    const cards = cardRefs.current.filter(Boolean);
    if (!track || cards.length === 0) return;

    const step =
      cards.length > 1
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : cards[0].offsetWidth;

    track.scrollBy({ left: arah * step, behavior: "smooth" });
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByCard(-1);
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByCard(1);
    }
  };

  return (
    /* overflow-hidden DILARANG di sini. Track di dalamnya memakai
       overflow-x-auto; kalau induknya ikut memotong, scroll-nya mati
       di sebagian browser. */
    <section className="border-t border-slate-200 bg-slate-50 py-12 sm:py-16 md:py-20">

      {/* Aturan jarak tepi. Ditaruh di dalam komponen supaya ikut ke
         mana pun komponen ini dipakai - tidak perlu menyentuh
         globals.css. */}
      <style dangerouslySetInnerHTML={{ __html: CSS_JARAK }} />

      {/* ---------- KEPALA ---------- */}
      <div className="coe-pad mb-8 flex flex-wrap items-end justify-between gap-x-8 gap-y-3 sm:mb-10">
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#15804a] sm:text-xs sm:tracking-[0.18em]">
            {t("coe.eyebrow")}
          </p>

          <h2 className="text-balance text-2xl font-bold leading-[1.15] tracking-tight text-[#20262c] sm:text-3xl md:text-4xl">
            {t("coe.judul")}
          </h2>

          <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600 sm:mt-3 sm:text-base">
            {t("coe.subjudul")}
          </p>
        </div>

        {LIHAT_SEMUA_HREF && (
          <Link href={LIHAT_SEMUA_HREF} className="group inline-flex shrink-0 items-center gap-2 text-sm font-bold text-[#0f6a3b] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#15804a] sm:text-base">
            {t("coe.lihatSemua")}
            <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transform-none">&rarr;</span>
          </Link>
        )}
      </div>

      {/* ---------- TRACK ----------
         Pembungkus .coe-pad inilah yang menentukan batas kiri dan
         kanan area guliran. Kartu yang melewati batas itu terpotong,
         bukan terlihat sampai tepi layar.

         PEMBUNGKUS INI TIDAK BOLEH DIBERI overflow apa pun. Yang
         menggulir adalah anaknya; kalau induknya ikut memotong secara
         eksplisit, scroll-nya mati di sebagian browser.

         items-stretch + kartu h-full: tinggi semua kartu mengikuti
         kartu tertinggi, jadi baris "Baca Selengkapnya" di dasarnya
         rata satu garis meski panjang deskripsinya berbeda. */}
      <div className="coe-pad">
        <div ref={trackRef} tabIndex={0} onKeyDown={onKeyDown} role="group" aria-label={t("coe.trackAria")} className="flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#15804a] [&::-webkit-scrollbar]:hidden md:gap-6">
          {FASILITAS.map((item, i) => (
            <div key={item.key} ref={(el) => (cardRefs.current[i] = el)} className="w-[76%] shrink-0 snap-start sm:w-[44%] md:w-[30%] lg:w-[22%] xl:w-[18%]">
              <KartuFasilitas item={item} t={t} />
            </div>
          ))}
        </div>
      </div>

      {/* ---------- KONTROL ----------
         Panah TETAP TERLIHAT saat nonaktif, hanya diredupkan: dalam
         satu baris bersama tombol tengah, tombol yang hilang membuat
         barisnya berubah lebar dan tombol tengahnya melompat. */}
      <div className="coe-pad mt-8 flex items-center justify-between gap-4 sm:mt-10">
        <TombolPanah arah="kiri" onClick={() => scrollByCard(-1)} disabled={nav.atStart} t={t} />

        <Link href={AJAK_HREF} className="rounded-lg bg-[#0f6a3b] px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#20262c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#15804a] sm:px-8 sm:text-base">
          {t("coe.ajak")}
        </Link>

        <TombolPanah arah="kanan" onClick={() => scrollByCard(1)} disabled={nav.atEnd} t={t} />
      </div>

    </section>
  );
}

/* ============================================================
   KARTU
   ============================================================ */

function KartuFasilitas({ item, t }) {
  const nama = t(`coe.f.${item.key}`);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_1px_2px_rgba(47,4,37,0.12),0_16px_32px_-12px_rgba(47,4,37,0.22)] hover:ring-[#bfe3cf] motion-reduce:transform-none">

      <GambarKartu item={item} t={t} />

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <h3 className="mb-2 text-[15px] font-bold leading-snug text-[#20262c] transition-colors duration-200 group-hover:text-[#0f6a3b] sm:text-base md:text-lg">
          {item.href ? (
            /* ::after absolut membuat SELURUH kartu bisa diklik tanpa
               membungkus kartu dalam <a>. Membungkus seluruh kartu
               akan membuat pembaca layar membacakan judul dan
               deskripsi sebagai satu nama tautan yang sangat panjang. */
            <Link href={item.href} className="after:absolute after:inset-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#15804a]">
              {nama}
            </Link>
          ) : (
            nama
          )}
        </h3>

        <p className="mb-4 line-clamp-3 text-[13px] leading-relaxed text-slate-600 sm:text-sm">
          {t(`coe.f.${item.key}Desc`)}
        </p>

        {/* mt-auto mendorong baris ini ke dasar kartu, jadi posisinya
           rata meski panjang deskripsinya berbeda. aria-hidden karena
           tautan sebenarnya sudah ada di judul - tanpa ini, pembaca
           layar mendengar dua tautan ke halaman yang sama. */}
        {item.href && (
          <span aria-hidden="true" className="mt-auto inline-flex items-center gap-2 text-[13px] font-bold text-[#0f6a3b] sm:text-sm">
            {t("coe.baca")}
            <span className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transform-none">&rarr;</span>
          </span>
        )}
      </div>
    </article>
  );
}

/* ============================================================
   GAMBAR KARTU

   Kalau berkasnya belum ada, yang tampil adalah kotak penampung yang
   MENULISKAN NAMA BERKAS yang sedang ia tunggu - bukan ikon gambar
   rusak tanpa keterangan.
   ============================================================ */

function GambarKartu({ item, t }) {
  if (!item.image) {
    return (
      <div className="flex aspect-[16/10] w-full items-center justify-center bg-[#f1faf5] p-4 text-center">
        <p className="text-[12px] font-semibold text-[#15804a]">{t("coe.fotoKosong")}</p>
      </div>
    );
  }

  return (
    <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#f1faf5]">
      {/* Digambar DI BELAKANG foto. Begitu berkasnya ada, foto
         menutupinya sepenuhnya - dan sekarang benar-benar
         sepenuhnya, karena object-cover tidak menyisakan pita
         kosong. Lihat catatan di atas soal teks yang dulu bocor. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center">
        <p className="text-[11px] font-bold text-[#20262c]">Simpan foto di:</p>
        <code className="break-all text-[11px] font-semibold text-[#15804a]">
          public{item.image}
        </code>
      </div>

      <Image
        src={item.image}
        alt=""
        fill
        sizes="(min-width: 1280px) 340px, (min-width: 768px) 40vw, 78vw"
        /* object-position lewat style, BUKAN kelas Tailwind.
           Tailwind hanya membangun kelas yang ia temukan sebagai
           teks utuh di kode; nilai yang datang dari variabel tidak
           akan pernah ikut terbangun dan kelasnya diam-diam hilang
           saat build produksi. */
        style={{ objectPosition: item.fokus || "50% 50%" }}
        className="relative object-cover transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transform-none"
      />
    </div>
  );
}

/* ============================================================
   TOMBOL PANAH

   44x44px - ambang minimum target sentuh yang lazim dipakai. Ukuran
   itu yang menentukan h-11 w-11, bukan selera.
   ============================================================ */

function TombolPanah({ arah, onClick, disabled, t }) {
  const kiri = arah === "kiri";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={kiri ? t("coe.sebelumnya") : t("coe.berikutnya")}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#0f6a3b] ring-1 ring-slate-200 transition hover:bg-[#f1faf5] disabled:pointer-events-none disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#15804a] motion-reduce:transition-none sm:h-12 sm:w-12"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
        <path d={kiri ? "M19 12H5m0 0 6-6m-6 6 6 6" : "M5 12h14m0 0-6-6m6 6-6 6"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
